/**
 * Frontend Logger
 *
 * A simple logging utility that can be toggled on/off via:
 * - localStorage.setItem('debug', 'true')  - Enable logging
 * - localStorage.setItem('debug', 'false') - Disable logging
 * - In browser console: window.logger.enable() / window.logger.disable()
 *
 * Logging is disabled by default in production.
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  SILENT = 4,
}

const LOG_LEVEL_NAMES: Record<LogLevel, string> = {
  [LogLevel.DEBUG]: 'DEBUG',
  [LogLevel.INFO]: 'INFO',
  [LogLevel.WARN]: 'WARN',
  [LogLevel.ERROR]: 'ERROR',
  [LogLevel.SILENT]: 'SILENT',
};

const LOG_STORAGE_KEY = 'debug';
const LOG_LEVEL_KEY = 'logLevel';

class Logger {
  private enabled: boolean;
  private level: LogLevel;

  constructor() {
    this.enabled = this.getEnabledFromStorage();
    this.level = this.getLevelFromStorage();

    // Expose logger controls to window for easy access in browser console
    if (typeof window !== 'undefined') {
      (window as unknown as { logger: Logger }).logger = this;
    }
  }

  private getEnabledFromStorage(): boolean {
    if (typeof localStorage === 'undefined') return false;

    const stored = localStorage.getItem(LOG_STORAGE_KEY);
    if (stored !== null) {
      return stored === 'true';
    }

    // Default: enabled in development, disabled in production
    return import.meta.env.DEV;
  }

  private getLevelFromStorage(): LogLevel {
    if (typeof localStorage === 'undefined') return LogLevel.DEBUG;

    const stored = localStorage.getItem(LOG_LEVEL_KEY);
    if (stored !== null) {
      const level = parseInt(stored, 10);
      if (level >= LogLevel.DEBUG && level <= LogLevel.SILENT) {
        return level;
      }
    }

    return LogLevel.DEBUG;
  }

  private shouldLog(level: LogLevel): boolean {
    return this.enabled && level >= this.level;
  }

  private formatMessage(level: LogLevel, message: string): string {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, -1);
    return `[${timestamp}] [${LOG_LEVEL_NAMES[level]}] ${message}`;
  }

  /**
   * Enable logging (persists to localStorage)
   */
  enable(): void {
    this.enabled = true;
    localStorage.setItem(LOG_STORAGE_KEY, 'true');
    console.log('%c[Logger] Logging enabled', 'color: green; font-weight: bold');
  }

  /**
   * Disable logging (persists to localStorage)
   */
  disable(): void {
    this.enabled = false;
    localStorage.setItem(LOG_STORAGE_KEY, 'false');
    console.log('%c[Logger] Logging disabled', 'color: red; font-weight: bold');
  }

  /**
   * Set minimum log level
   */
  setLevel(level: LogLevel): void {
    this.level = level;
    localStorage.setItem(LOG_LEVEL_KEY, String(level));
    console.log(
      `%c[Logger] Log level set to ${LOG_LEVEL_NAMES[level]}`,
      'color: blue; font-weight: bold'
    );
  }

  /**
   * Check if logging is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Get current log level
   */
  getLevel(): LogLevel {
    return this.level;
  }

  /**
   * Log debug message (for detailed debugging info)
   */
  debug(message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(`%c${this.formatMessage(LogLevel.DEBUG, message)}`, 'color: gray', ...args);
    }
  }

  /**
   * Log info message (for general information)
   */
  info(message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.log(`%c${this.formatMessage(LogLevel.INFO, message)}`, 'color: blue', ...args);
    }
  }

  /**
   * Log warning message
   */
  warn(message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(this.formatMessage(LogLevel.WARN, message), ...args);
    }
  }

  /**
   * Log error message (always logs unless SILENT)
   */
  error(message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(this.formatMessage(LogLevel.ERROR, message), ...args);
    }
  }

  /**
   * Log a group of related messages
   */
  group(label: string, fn: () => void): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.group(label);
      fn();
      console.groupEnd();
    }
  }

  /**
   * Log timing for a function
   */
  async time<T>(label: string, fn: () => Promise<T>): Promise<T> {
    if (!this.shouldLog(LogLevel.DEBUG)) {
      return fn();
    }

    const start = performance.now();
    try {
      const result = await fn();
      const duration = (performance.now() - start).toFixed(2);
      this.debug(`${label} completed in ${duration}ms`);
      return result;
    } catch (error) {
      const duration = (performance.now() - start).toFixed(2);
      this.error(`${label} failed after ${duration}ms`, error);
      throw error;
    }
  }

  /**
   * Print help information
   */
  help(): void {
    console.log(
      `
%c=== Logger Help ===

Commands (use in browser console):
  logger.enable()     - Enable logging
  logger.disable()    - Disable logging
  logger.setLevel(n)  - Set log level (0=DEBUG, 1=INFO, 2=WARN, 3=ERROR, 4=SILENT)
  logger.isEnabled()  - Check if logging is enabled
  logger.getLevel()   - Get current log level
  logger.help()       - Show this help

Current state:
  Enabled: ${this.enabled}
  Level: ${LOG_LEVEL_NAMES[this.level]} (${this.level})
`,
      'color: cyan'
    );
  }
}

// Export singleton instance
export const logger = new Logger();

// Export LogLevel for external use
export { LogLevel as Level };
